import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Card, CardBody, Button, Alert, Spinner } from 'reactstrap';
import { userProfileApi } from '../../services/userProfileApi';
import { useAnalytics, ANALYTICS_EVENTS } from '../../analytics';
import { getPostPaymentRedirect, clearPostPaymentRedirect } from '../../utils/loginRedirect';

// Import Breadcrumb
import Breadcrumb from '../../components/Common/Breadcrumb';

const REPORT_RETURN_DELAY_MS = 2500;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [planDetails, setPlanDetails] = useState(null);

  const sessionId = searchParams.get('session_id');

  // Checkout started from somewhere that wants the user back (e.g. a shared
  // report's Unlock) — read once, then clear so a revisit of this URL
  // doesn't bounce them again.
  const [returnPath] = useState(() => getPostPaymentRedirect());

  useEffect(() => {
    clearPostPaymentRedirect();
    handlePaymentSuccess();
  }, []);

  // `upgraded=1` tells the report to re-check (and briefly retry) for its
  // now-unlocked content, since Stripe's webhook can lag the redirect.
  const returnToReport = () => {
    const sep = returnPath.includes('?') ? '&' : '?';
    navigate(`${returnPath}${sep}upgraded=1`, { replace: true });
  };

  useEffect(() => {
    if (loading || error || !returnPath) return;
    const timer = setTimeout(returnToReport, REPORT_RETURN_DELAY_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, error, returnPath]);

  const handlePaymentSuccess = async () => {
    try {
      setLoading(true);
      
      // Verify payment with backend (optional but recommended)
      if (sessionId) {
        // You can add a backend call here to verify the payment
        // const verification = await userProfileApi.verifyPayment(sessionId);
      }

      // Refresh user's current plan data
      const currentPlanResult = await userProfileApi.fetchCurrentPlan();
      const plansResult = await userProfileApi.fetchPlans();
      
      // Find the plan details
      let plan = null;
      if (currentPlanResult.data && plansResult.data) {
        const currentPlanId = currentPlanResult.data.product;
        plan = plansResult.data.find(p => p.id === currentPlanId);
        if (plan) {
          setPlanDetails(plan);
        }
      }

      // Stripe checkout lands here on success. Fire once per checkout
      // session so a refresh/revisit of this URL doesn't double-count.
      const trackedKey = `plan_upgrade_completed_${sessionId || 'no_session'}`;
      let alreadyTracked = false;
      try { alreadyTracked = !!sessionId && localStorage.getItem(trackedKey) === '1'; } catch (e) { }
      if (!alreadyTracked) {
        trackEvent(ANALYTICS_EVENTS.CONVERSION.PLAN_UPGRADE_COMPLETED, {
          plan_id: plan?.id || currentPlanResult.data?.product,
          plan_name: plan?.name,
          value: Number(plan?.price) || undefined,
          currency: plan?.price != null ? 'USD' : undefined,
          transaction_id: sessionId || undefined,
        });
        try { if (sessionId) localStorage.setItem(trackedKey, '1'); } catch (e) { }
      }

      setLoading(false);
    } catch (err) {
      console.error('Error handling payment success:', err);
      setError('There was an issue processing your payment. Please contact support.');
      setLoading(false);
    }
  };

  const handleContinue = () => {
    navigate('/dashboard'); // or wherever you want to redirect
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <Row className="justify-content-center">
            <Col md={8} lg={6}>
              <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                <div className="text-center">
                  <Spinner color="primary" size="lg" />
                  <p className="mt-3">Processing your payment...</p>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
          <Col md={8} lg={6}>
            <Card 
              className="shadow"
              style={{
                background: '#21222D',
                border: '1px solid #353748',
                borderRadius: '8px'
              }}
            >
              <CardBody className="text-center p-5">
                {/* Success Icon */}
                <div className="mb-4">
                  <div 
                    className="rounded-circle d-inline-flex align-items-center justify-content-center" 
                    style={{ 
                      width: '80px', 
                      height: '80px',
                      background: 'linear-gradient(135deg, #F6255A, grey)'
                    }}
                  >
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" 
                            stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>

                {/* Success Message */}
                <h2 className="mb-3" style={{ fontFamily: 'Rubik', fontWeight: 600, color: '#FFFFFF' }}>
                  Payment Successful!
                </h2>
                <p className="mb-4" style={{ color: '#FFFFFF' }}>
                  Thank you for your purchase. Your subscription has been activated successfully.
                  {returnPath && !error && (
                    <>
                      <br />
                      <span style={{ color: '#A1ACB8' }}>Taking you back to your report…</span>
                    </>
                  )}
                </p>

                {/* Plan Details */}
                {planDetails && (
                  <div 
                    className="rounded p-4 mb-4"
                    style={{
                      background: '#171821',
                      border: '1px solid #353748'
                    }}
                  >
                    <h5 className="mb-3" style={{ fontFamily: 'Rubik', fontWeight: 600, color: '#fff' }}>
                      Your New Plan
                    </h5>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="mb-1" style={{ color: '#fff' }}>{planDetails.name}</h6>
                        <p className="text-muted mb-0">${planDetails.price}/month</p>
                      </div>
                      <div className="text-end">
                        <span 
                          className="badge"
                          style={{
                            background: 'linear-gradient(135deg, #28a745, #20c997)',
                            color: 'white',
                            padding: '8px 12px',
                            borderRadius: '6px'
                          }}
                        >
                          Active
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Session ID (for debugging) */}
                {sessionId && (
                  <div className="mb-4">
                    <small className="text-muted">
                      Session ID: {sessionId}
                    </small>
                  </div>
                )}

                {/* Error Display */}
                {error && (
                  <Alert 
                    color="warning" 
                    className="mb-4"
                    style={{
                      background: '#fff3cd',
                      border: '1px solid #ffeaa7',
                      color: '#856404'
                    }}
                  >
                    {error}
                  </Alert>
                )}

                {/* Action Buttons */}
                <div className="d-grid gap-3">
                  <Button 
                    color="primary" 
                    size="lg" 
                    onClick={returnPath ? returnToReport : handleContinue}
                    style={{
                      background: '#F6255A',
                      border: 'none',
                      borderRadius: '20px',
                      padding: '12px 24px',
                      fontFamily: 'Rubik',
                      fontWeight: 600,
                      fontSize: '16px',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#e11d4c';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = '#F6255A';
                    }}
                  >
                    {returnPath ? 'Back to Report' : 'Continue to Dashboard'}
                  </Button>
                  <Button 
                    color="outline-secondary" 
                    onClick={() => navigate('/profile')}
                    style={{
                      background: '#171821',
                      border: '1px solid #353748',
                      color: '#A1ACB8',
                      borderRadius: '20px',
                      padding: '12px 24px',
                      fontFamily: 'Rubik',
                      fontWeight: 500,
                      fontSize: '16px',
                      transition: 'all 0.2s ease',
                      color: '#FFFFFF'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#2A2C38';
                      e.target.style.borderColor = '#4A4C58';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = '#171821';
                      e.target.style.borderColor = '#353748';
                    }}
                  >
                    View My Plans
                  </Button>
                </div>

                {/* Additional Info */}
                <div className="mt-4">
                  <small style={{ color: '#FFFFFF' }}>
                    You will receive a confirmation email shortly. 
                    If you have any questions, please contact our support team.
                  </small>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}